import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';

import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import Prism from 'prismjs';
import 'prismjs/components/prism-python';
import 'prismjs/themes/prism-tomorrow.css';

import { themeColors } from '../../styles/theme';
import { NODE_COLORS } from '../../config/theme';

const FONT = 'Menlo, Monaco, "SFMono-Regular", Consolas, "Liberation Mono", "Courier New", monospace';
const FONT_SIZE = '13.5px';
const LINE_HEIGHT = '1.65';
const GUTTER_WIDTH = 48; // px — line number gutter
const PAD_V = '16px';
const PAD_H = '16px';
const PAD_LEFT = `${GUTTER_WIDTH + 12}px`; // gutter + gap

const DEFAULT_CODE = `def process(input_data):
    """
    Process input data from connected nodes.

    Args:
        input_data (dict): outputs from all connected nodes, keyed by node label.

    Returns:
        str | dict | list: the output of this node.
    """
    # Access a connected node's output by its label:
    # value = input_data.get("My Node Label")

    return input_data
`;

export default function CodeEditor({ open, onClose, selectedNode, onSave }) {
  const [code, setCode] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const textareaRef = useRef(null);
  const preRef = useRef(null);
  const highlightTimer = useRef(null);
  const codeRef = useRef(code);
  useEffect(() => { codeRef.current = code; }, [code]);

  // Initialise from node data when modal opens; reset fullscreen on close
  useEffect(() => {
    if (open) {
      setCode(selectedNode?.data?.code || DEFAULT_CODE);
    } else {
      setIsFullscreen(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-highlight whenever code changes or modal opens
  useEffect(() => {
    if (!open) return;
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => Prism.highlightAll(), 10);
    return () => clearTimeout(highlightTimer.current);
  }, [code, open]);

  // Sync pre scroll to match textarea
  const syncScroll = () => {
    if (textareaRef.current && preRef.current) {
      preRef.current.scrollTop = textareaRef.current.scrollTop;
      preRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  // Save without closing — used by Cmd+S
  const quickSave = useCallback(() => {
    onSave?.(codeRef.current);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  }, [onSave]);

  // Save and close — used by the Save button
  const handleSave = useCallback(() => {
    onSave?.(code);
    onClose();
  }, [code, onSave, onClose]);

  // Ctrl+S / Cmd+S — save in place, never close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        quickSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, quickSave]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      setCode(code.substring(0, start) + '    ' + code.substring(end));
      setTimeout(() => {
        ta.selectionStart = ta.selectionEnd = start + 4;
      }, 0);
    }
  };

  const lineCount = (code.match(/\n/g) || []).length + 1;
  const charCount = code.length;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          width: isFullscreen ? '100vw' : 'min(1000px, 90vw)',
          height: isFullscreen ? '100vh' : 'min(760px, 88vh)',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
          borderRadius: isFullscreen ? 0 : '14px',
          border: isFullscreen ? 'none' : '1px solid #3a3a3a',
          boxShadow: isFullscreen ? 'none' : '0 32px 64px -12px rgba(0,0,0,0.5), 0 12px 28px -6px rgba(0,0,0,0.3)',
          backgroundColor: '#1e1e1e',
          transition: 'width 0.18s ease, height 0.18s ease, border-radius 0.18s ease',
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            height: 60,
            borderBottom: '1px solid #3a3a3a',
            flexShrink: 0,
            backgroundColor: '#1e1e1e',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                color: '#64b5f6',
                flexShrink: 0,
              }}
            >
              <CodeOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: '#e0e0e0', lineHeight: 1.2 }}>
                Code Editor
              </Typography>
              {selectedNode?.data?.label && (
                <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 0.25, lineHeight: 1.2 }}>
                  {selectedNode.data.label}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} placement="bottom">
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => setIsFullscreen((f) => !f)}
                sx={{ borderRadius: '8px', color: '#666', '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#aaa' } }}
              >
                {isFullscreen
                  ? <FullscreenExitIcon sx={{ fontSize: 18 }} />
                  : <FullscreenIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <IconButton
              size="sm"
              variant="plain"
              onClick={onClose}
              sx={{ borderRadius: '8px', color: '#666', '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#aaa' } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Editor ── */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', backgroundColor: '#2d2d2d' }}>
          {/*
            Syntax-highlighted display layer.
            Line numbers are an absolutely-positioned div inside this <pre> so they
            scroll vertically in sync with the code content automatically.
          */}
          <pre
            ref={preRef}
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              margin: 0,
              paddingTop: PAD_V,
              paddingBottom: PAD_V,
              paddingLeft: PAD_LEFT,
              paddingRight: PAD_H,
              background: '#2d2d2d',
              color: '#ccc',
              fontFamily: FONT,
              fontSize: FONT_SIZE,
              lineHeight: LINE_HEIGHT,
              overflow: 'auto',
              whiteSpace: 'pre',
              tabSize: 4,
              pointerEvents: 'none',
              boxSizing: 'border-box',
              zIndex: 1,
            }}
          >
            {/* Gutter — inside pre so it scrolls vertically with the content */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: GUTTER_WIDTH,
                minHeight: '100%',
                paddingTop: PAD_V,
                paddingBottom: PAD_V,
                background: '#222',
                borderRight: '1px solid #3a3a3a',
                textAlign: 'right',
                userSelect: 'none',
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                lineHeight: LINE_HEIGHT,
                color: '#555',
                boxSizing: 'border-box',
              }}
            >
              {code.split('\n').map((_, i) => (
                <div key={i} style={{ paddingRight: 8 }}>{i + 1}</div>
              ))}
            </div>

            <code className="language-python">{code}</code>
          </pre>

          {/* Transparent edit layer */}
          <textarea
            ref={textareaRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={handleKeyDown}
            onScroll={syncScroll}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            data-gramm="false"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              paddingTop: PAD_V,
              paddingBottom: PAD_V,
              paddingLeft: PAD_LEFT,
              paddingRight: PAD_H,
              color: 'rgba(255,255,255,0.01)',
              caretColor: '#fff',
              background: 'transparent',
              fontFamily: FONT,
              fontSize: FONT_SIZE,
              lineHeight: LINE_HEIGHT,
              whiteSpace: 'pre',
              tabSize: 4,
              overflow: 'auto',
              resize: 'none',
              border: 'none',
              outline: 'none',
              boxSizing: 'border-box',
              margin: 0,
              zIndex: 2,
            }}
          />
        </Box>

        {/* ── Footer ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            height: 52,
            borderTop: '1px solid #3a3a3a',
            backgroundColor: '#222',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '0.6875rem', color: savedFlash ? '#4ade80' : '#555', fontFamily: FONT, transition: 'color 0.15s ease' }}>
            {savedFlash ? 'Saved ✓' : `${charCount.toLocaleString()} chars · ${lineCount} ${lineCount === 1 ? 'line' : 'lines'} · Tab = 4 spaces`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy'} placement="top">
              <IconButton
                size="sm"
                variant="plain"
                onClick={handleCopy}
                sx={{
                  borderRadius: '7px',
                  color: copySuccess ? '#4ade80' : '#666',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#999' },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>

            <Button
              variant="plain"
              size="sm"
              onClick={onClose}
              sx={{
                borderRadius: '7px',
                fontWeight: 500,
                color: '#888',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#aaa' },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="solid"
              size="sm"
              onClick={handleSave}
              sx={{
                borderRadius: '7px',
                fontWeight: 600,
                fontSize: '0.8125rem',
                backgroundColor: themeColors.primary[500],
                color: 'white',
                '&:hover': { backgroundColor: themeColors.primary[600] },
                px: 2,
                height: 34,
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
